import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { StoreFormValues } from './schemas';
import type { StoreConfiguration } from './types';

import { apiRequest, ApiError } from '@/lib/api-client';
import { env } from '@/lib/env';
import type { ImageSizeVerdict } from '@/lib/image-size';
import { useSession } from '@/lib/session';

export type BrandingKind = 'logo' | 'banner';

/**
 * `logoUrl`/`bannerUrl` are deliberately absent — Upload réel du
 * logo/bannière lot removed both from `storeConfigurationSchema`
 * (`store.dto.ts`) entirely. A merchant configures either through
 * `useUploadStoreBrandingMutation` below (a real file upload), never
 * through this full-replace PUT body.
 */
function storeToBody(values: StoreFormValues): Record<string, unknown> {
  return {
    slug: values.slug,
    name: values.name,
    description: values.description,
    tagline: values.tagline,
    address: values.address,
    phone: values.phone,
    whatsapp: values.whatsapp,
    aboutTitle: values.aboutTitle,
    aboutBody: values.aboutBody,
    email: values.email,
    openingHours: values.openingHours,
    returnPolicy: values.returnPolicy,
    shippingPolicy: values.shippingPolicy,
    termsOfService: values.termsOfService,
    privacyPolicy: values.privacyPolicy,
    seoTitle: values.seoTitle,
    seoDescription: values.seoDescription,
    stockDisplayPolicy: values.stockDisplayPolicy,
    deliveryFee: values.deliveryFee,
    colorPrimary: values.colorPrimary,
    colorSecondary: values.colorSecondary,
    colorAccent: values.colorAccent,
    colorText: values.colorText,
    colorButton: values.colorButton,
    colorBadge: values.colorBadge,
    colorPromo: values.colorPromo,
    colorVariant: values.colorVariant,
    metaPixelId: values.metaPixelId,
    socialFacebook: values.socialFacebook,
    socialInstagram: values.socialInstagram,
    socialTiktok: values.socialTiktok,
    socialYoutube: values.socialYoutube,
    socialX: values.socialX,
    socialLinkedin: values.socialLinkedin,
  };
}

/**
 * `GET /api/v1/merchant/store` (docs/resumée.txt entrée 58) answers 404
 * for a merchant who has never configured a store yet — a real, expected
 * state, not a failure. A 404 here resolves to `null`, distinct from
 * `undefined` (query not yet run/disabled): `StorePage` reads `null` as
 * "show the first-time setup form", any other thrown error as a genuine
 * failure (`isError`).
 */
export function useMyStoreQuery(): UseQueryResult<StoreConfiguration | null, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['store', activeShopId],
    queryFn: async () => {
      try {
        return await apiRequest<StoreConfiguration>('/merchant/store', {
          accessToken,
          shopId: activeShopId,
        });
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: accessToken !== null && activeShopId !== null,
  });
}

export function useUpdateStoreMutation(): UseMutationResult<
  StoreConfiguration,
  ApiError,
  StoreFormValues
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: StoreFormValues) =>
      apiRequest<StoreConfiguration>('/merchant/store', {
        method: 'PUT',
        body: storeToBody(values),
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['store', activeShopId] });
    },
  });
}

/**
 * Upload réel du logo/bannière lot — `POST /merchant/store/branding/:kind`,
 * mirroring `useUploadProductImageMutation` (features/products/api.ts)
 * exactly: multipart body, field name `"file"`. Invalidates the store
 * query on success so `StoreForm`'s own preview picks up the new
 * `logoObjectKey`/`bannerObjectKey` immediately, the same "refetch, never
 * hand-patch the cache" posture that mutation already uses.
 */
export interface BrandingUploadResult {
  readonly store: StoreConfiguration;
  /** The API's verdict on the picture's size (absent only from an older API). */
  readonly size: ImageSizeVerdict | undefined;
}

export function useUploadStoreBrandingMutation(
  kind: BrandingKind,
): UseMutationResult<BrandingUploadResult, ApiError, File> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const uploaded = await apiRequest<{ objectKey: string; size?: ImageSizeVerdict }>(
        `/merchant/store/branding/${kind}`,
        { method: 'POST', body: form, accessToken, shopId: activeShopId },
      );
      const store = await apiRequest<StoreConfiguration>('/merchant/store', {
        accessToken,
        shopId: activeShopId,
      });
      return { store, size: uploaded.size };
    },
    onSuccess: ({ store }) => {
      queryClient.setQueryData(['store', activeShopId], store);
    },
  });
}

/**
 * Authenticated preview for an uploaded logo/banner — mirrors
 * `fetchProductImageObjectUrl` (features/products/api.ts) exactly: the
 * merchant `raw` route requires `Authorization`/`X-Shop-Id`, which a plain
 * `<img src>` cannot send, so this fetches the bytes as a blob and hands
 * the caller an object URL to use as `src` instead. The API 302-redirects
 * this route to a momentarily-signed S3 URL; `fetch` follows that
 * transparently. The caller owns the returned object URL's lifecycle
 * (must `URL.revokeObjectURL` it when done).
 */
export async function fetchStoreBrandingObjectUrl(
  kind: BrandingKind,
  accessToken: string,
  shopId: string,
): Promise<string> {
  const response = await fetch(`${env.apiBaseUrl}/api/v1/merchant/store/branding/${kind}/raw`, {
    headers: { Authorization: `Bearer ${accessToken}`, 'X-Shop-Id': shopId },
  });
  if (!response.ok) {
    throw new Error(`Failed to load ${kind} (status ${String(response.status)}).`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}
